import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FiPrinter } from 'react-icons/fi';
import { startSetStock } from '../../../actions/CurrentStock';
import EmptyState from '../EmptyState';
import { INVOICES, BILLING, GST_RATE, money, invoiceTotal, useClient } from './clientSettingsData';

// /invoice/{ticker:exchange}/{key}, opened in a new tab from Billing › Invoices › PDF: just the tax invoice, no navbar or tabs
// (AppRouter leaves the header out). Print / Save as PDF uses the browser's print dialog. Sample data until there is a billing API.
const InvoiceView: React.FC = () => {
    const dispatch = useDispatch();
    const { ticker, id } = useParams<{ ticker: string; id: string }>();
    const { stock, name } = useClient();
    const loaded = !!stock && `${stock.ticker}:${stock.exchange}` === ticker;
    const invoice = INVOICES.find(i => i.key === id);

    // no header here to load the current client, so load the one in the url
    useEffect(() => {
        if (!loaded) dispatch(startSetStock(ticker));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticker]);

    if (!loaded) return null;

    if (!invoice)
        return <div className="v2-page invoice-view">
            <EmptyState art="caughtUp" title="Invoice not found" sub="Open it again from Client settings › Billing › Invoices." />
        </div>;

    const gst = invoiceTotal(invoice) - invoice.subtotal;
    const b = BILLING;

    return <div className="v2-page invoice-view">
        <div className="invoice-toolbar">
            <span className="file">{invoice.id}.pdf</span>
            <button type="button" className="btn-primary with-icon" onClick={() => window.print()}><FiPrinter /> Print / Save as PDF</button>
        </div>

        <article className="invoice-sheet">
            <header className="inv-head">
                <img src="/images/logo-dark.svg" alt="Investi" className="inv-logo" />
                <div className="inv-title">
                    <h1>Tax Invoice</h1>
                    <span className="sample">Sample</span>
                </div>
            </header>

            <div className="inv-parties">
                <div>
                    <div className="inv-label">From</div>
                    <strong>Investi Services</strong>
                    <span>ABN 00 000 000 000</span>
                    <span>accounts@investi.com.au</span>
                </div>
                <div>
                    <div className="inv-label">Billed to</div>
                    <strong>{name}</strong>
                    <span>ABN {b.abn}</span>
                    <span>Attn: {b.contact}</span>
                    <span>{b.address}</span>
                    <span>{b.suburb} {b.state} {b.postcode}</span>
                    <span>{b.email}</span>
                </div>
                <dl className="inv-meta">
                    <dt>Invoice number</dt><dd>{invoice.id}</dd>
                    <dt>Invoice date</dt><dd>{invoice.date}</dd>
                    {b.po && <><dt>PO number</dt><dd>{b.po}</dd></>}
                    <dt>Status</dt><dd className="paid">Paid {invoice.paid}</dd>
                </dl>
            </div>

            <table className="inv-lines">
                <thead>
                    <tr><th>Description</th><th className="num">Qty</th><th className="num">Unit price</th><th className="num">Amount</th></tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <strong>Investi {invoice.plan} plan — annual subscription</strong>
                            <span>{invoice.period} · 12 months for the price of 10</span>
                        </td>
                        <td className="num">1</td>
                        <td className="num">{money(invoice.subtotal)}</td>
                        <td className="num">{money(invoice.subtotal)}</td>
                    </tr>
                </tbody>
            </table>

            <dl className="inv-totals">
                <dt>Subtotal</dt><dd>{money(invoice.subtotal)}</dd>
                <dt>GST ({GST_RATE * 100}%)</dt><dd>{money(gst)}</dd>
                <dt className="total">Total (AUD)</dt><dd className="total">{money(invoiceTotal(invoice))}</dd>
                <dt>Amount paid</dt><dd>{money(invoiceTotal(invoice))}</dd>
                <dt className="due">Balance due</dt><dd className="due">{money(0)}</dd>
            </dl>

            <footer className="inv-foot">
                <p>Paid by card on file on {invoice.paid}. Thank you for your business.</p>
                <p>Questions about this invoice? Email accounts@investi.com.au and quote {invoice.id}.</p>
            </footer>
        </article>
    </div>;
};

export default InvoiceView;
